import type { Theme } from '@glitz/core';
import { ThemeProvider as GlitzThemeProvider } from '@glitz/react';
import * as React from 'react';
import {
  a11yGrey,
  black,
  darkGrey,
  green,
  grey,
  orange,
  paleBlack,
  secondaryGrey,
  shadedGrey,
  sharedFontSize,
  sharedSpacing,
  softWhite,
  tintedBlack,
  white,
} from 'webapp/shared/sharedTheme';

export const secondaryLight = '#ffc166';
export const secondaryBrighter = '#ffe0b3';
export const secondaryBrightest = '#fff0d9';

export const monochrome = '#F3F3F3';
export const monochromeLight = '#ebebeb';
export const monochromeBrighter = '#f5f5f5';
export const creamWhite = '#FFF1D9';

export const red = '#D90005';

const primaryDark = '#1E865C';
const primaryColor = '#32A778';
const primaryLight = '#5DC79C';
const primaryBright = '#C5EFDE';

const primaryBackgroundLight = '#cef4eb';

const fireOrange = '#ff5000';
const fireOrangeDark = '#D64300';

const primary = {
  color: primaryDark,
  hover: primaryLight,
  text: white,
  hoverText: tintedBlack,
  border: primaryDark,
};

const tertiary = {
  color: white,
  hover: primaryBackgroundLight,
  text: tintedBlack,
  border: black,
  hoverBorder: black,
};

const fontSize = { xxxl: 56, xxl: 32, xl: 32, l: 28, m: 22, s: 18, xs: 16, xxs: 14, xxxs: 12 };

const fontWeight = {
  light: 300,
  normal: 400,
  primary: 500,
  bold: 600,
};

export const theme: Theme = {
  id: 'kitchentime',
  primary,
  secondary: primary,
  tertiary: tertiary,
  quaternary: primary,
  monochrome: {
    color: softWhite,
    hover: grey,
    text: black,
    border: softWhite,
  },
  monochromeBrighter: {
    color: monochromeBrighter,
    hover: '#eeeeee',
    text: black,
    border: monochromeBrighter,
  },
  positive: {
    color: '#4ec791',
    hover: '#36ab76',
    text: white,
    border: '#4ec791',
  },
  negative: {
    color: fireOrangeDark,
    hover: fireOrange,
    text: white,
    border: fireOrangeDark,
  },
  transparent: {
    color: 'transparent',
    hover: 'transparent',
    text: 'inherit',
    border: 'transparent',
  },
  white: {
    color: white,
    hover: primaryBright,
    text: tintedBlack,
    border: paleBlack,
    hoverBorder: paleBlack,
  },
  dark: {
    color: shadedGrey,
    hover: darkGrey,
    text: white,
    border: shadedGrey,
  },
  disabled: {
    color: monochromeBrighter,
    hover: monochromeBrighter,
    text: shadedGrey,
    border: monochromeBrighter,
  },
  myPagesMenu: {
    color: monochromeLight,
    fontWeight: fontWeight.bold,
  },
  myPagesLoyaltyPoints: {
    color: primaryBright,
    fontWeight: fontWeight.bold,
    textTransform: 'uppercase',
  },
  myPagesInfoBox: {
    color: monochromeLight,
  },
  myPagesProfilePage: {
    color: monochromeLight,
    textTransform: 'uppercase',
  },
  notificationColor: {
    color: fireOrangeDark,
    text: white,
  },
  productPageInfo: {
    color: primaryBackgroundLight,
  },
  fontFamily: {
    primary: 'cerebri',
    secondary: 'cerebri',
    tertiary: 'cerebri',
  },
  footer: {
    color: monochrome,
    fontSize: fontSize.xxs,
    heading: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.bold,
    },
    infoFooter: {
      color: tintedBlack,
      text: shadedGrey,
    },
    accordion: {
      backgroundColor: monochrome,
      borderColor: grey,
    },
  },
  memberClubNewsletter: {
    color: primaryColor,
    fontSize: fontSize.xs,
    text: 'white',
  },
  quickSearch: {
    borderColor: shadedGrey,
    backgroundColor: softWhite,
    borderRadius: 50,
    highlightText: black,
  },
  searchSuggestions: {
    borderRadius: 16,
    position: 40,
    borderColor: secondaryGrey,
    color: a11yGrey,
    backgroundColor: white,
  },
  price: {
    current: { card: { fontWeight: fontWeight.bold }, page: { fontWeight: fontWeight.bold } },
    original: { fontWeight: fontWeight.normal, fontSize: fontSize.xxs },
    discount: { color: red },
    vatText: { fontWeight: fontWeight.normal },
  },
  productCard: {
    brand: { fontSize: fontSize.xxs, fontWeight: fontWeight.bold },
    title: { fontSize: fontSize.xxs },
    price: { fontSize: fontSize.xxs },
    wishlistIcon: { color: primaryDark },
  },
  header: {
    backgroundColor: white,
    fontSize: fontSize.xxs,
    fontWeight: fontWeight.bold,
    menuTextTransform: 'initial',
    subCategoryFontSize: fontSize.xxs,
    menuHeadingFontSize: fontSize.s,
    menuHightlightColor: primaryBright,
    topMenu: { fontWeight: fontWeight.bold },
  },
  topBanner: {
    color: monochrome,
    text: 'inherit',
    checkColor: primaryDark,
  },
  fontWeight: {
    bold: fontWeight.bold,
    normal: fontWeight.normal,
    primary: fontWeight.bold,
    secondary: fontWeight.normal,
  },
  heading: {
    fontWeight: fontWeight.bold,
  },
  headerIndicators: {
    cart: {
      backgroundColor: primaryDark,
      color: white,
    },
    favourites: {
      backgroundColor: primaryDark,
      color: white,
    },
  },
  actionButton: primary,
  filterButton: primary,
  linkButton: tertiary,
  wishlistButton: {
    color: white,
    hover: primaryBright,
    text: tintedBlack,
    border: '#c9c0bb',
    hoverBorder: paleBlack,
  },
  carouselButton: {
    color: softWhite,
    hover: grey,
    text: black,
    border: softWhite,
  },
  secondaryCarouselButton: {
    color: white,
    hover: grey,
    text: softWhite,
    border: white,
  },
  bubble: { color: tintedBlack, backgroundColor: primaryBright },
  button: { textTransform: 'initial' },
  checkbox: {
    color: darkGrey,
    uncheckedColor: 'transparent',
    checkedColor: 'white',
    disabled: monochrome,
    borderColor: tintedBlack,
    checkedBackgroundColor: primaryDark,
  },
  sharedSpacing: sharedSpacing,
  spacing: { xxxxl: 70, xxxl: 70, xxl: 70, xl: 40, l: 24, m: 16, s: 12, xs: 8, xxs: 4 },
  sharedFontSize: sharedFontSize,
  fontSize: fontSize,
  radius: {
    primary: 0,
    secondary: 0,
  },
  monochromeLight,
  darkGrey: tintedBlack,
  secondaryBrighter,
  secondaryBrightest: primaryColor,
  secondaryLight,
  creamWhite,
  lightBackground: 'white',
  flyout: {
    headerColor: white,
    headerHeight: 100,
    headerTextPosition: 'start',
    backgroundColor: white,
  },
  borderColor: monochromeLight,
  borderHighlight: primaryColor,
  ratings: { backgroundColor: grey, color: orange },
  borderStrongColor: '#c9c0bb',
  breadcrumbs: {
    fontSizeLargeViewport: fontSize.xxs,
    fontSizeBase: fontSize.xxs,
    segmentFontWeight: 550,
    lastSegmentColor: darkGrey,
    color: darkGrey,
  },
  stockStatus: {
    inStock: green,
    lowInStock: orange,
    outOfStock: red,
    unknown: shadedGrey,
  },
  newsBadge: {
    color: black,
    text: white,
  },
  thumbnailBorder: monochromeLight,
  mobileMenu: {
    backgroundColor: white,
    accentColor: softWhite,
    borderColor: softWhite,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.bold,
    tabs: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.normal,
      textTransform: 'initial',
    },
  },
  checkout: {
    backgroundColor: monochromeBrighter,
    text: 'inherit',
  },
  facets: {
    flyoutHeader: { fontSize: fontSize.m, fontWeight: fontWeight.normal },
    heading: { fontSize: fontSize.xs, fontWeight: fontWeight.normal },
    facet: { fontSize: fontSize.xxs },
    count: { fontSize: fontSize.xxs, color: shadedGrey },
    borderColor: grey,
    search: {
      color: white,
      borderColor: shadedGrey,
    },
    range: {
      sliderTrackColor: primaryBright,
      sliderMarkColor: primary.color,
      sliderRailColor: softWhite,
      markDiameter: 16,
      railHeight: 4,
    },
    adornment: {
      input: { color: shadedGrey },
      affix: { fontSize: sharedFontSize.xs, color: shadedGrey },
    },
  },
  scrollbar: {
    trackColor: softWhite,
    thumbColor: shadedGrey,
    radius: 0,
  },
  imageBackgroundColor: 'rgba(128, 122, 106, 0.06)',
  focalPoint: {
    iconBackgroundColor: monochromeBrighter,
    imageBackgroundColor: white,
  },
  outline: paleBlack,
  inputFocusOutlineColor: primaryDark,
  materialSamples: {
    titleFontWeight: fontWeight.normal,
    descriptionFontWeight: fontWeight.light,
    stockStatusFontWeight: fontWeight.normal,
  },
};

export function ThemeProvider(props: React.PropsWithChildren<{}>) {
  return <GlitzThemeProvider theme={theme}>{props.children}</GlitzThemeProvider>;
}
